import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import HttpService from '../services/httpService';
import { setSnackInfo } from '../redux/actions/appAction';

const MAIN_HEADER_BG = '#1e3a8a';
const MAIN_HEADER_FG = '#ffffff';
const BORDER_LIGHT = '#e2e8f0';
const CELL_BORDER = `1px solid ${BORDER_LIGHT}`;
const ROW_STRIPE_A = '#ffffff';
const ROW_STRIPE_B = '#f8fafc';
const TEXT_PRIMARY = '#0f172a';

const headerCellBase = {
  backgroundColor: MAIN_HEADER_BG,
  color: MAIN_HEADER_FG,
  fontWeight: 700,
  border: CELL_BORDER,
  padding: '10px 12px',
  textAlign: 'left',
};

const DETAIL_COLUMNS = [
  { key: 'status', label: 'Status' },
  { key: 'statusDate', label: 'Status Date' },
  { key: 'department', label: 'Department' },
  { key: 'city', label: 'City' },
  { key: 'ec', label: 'EC' },
  { key: 'role', label: 'Role' },
  { key: 'name', label: 'Name' },
  { key: 'joiningDate', label: 'Joining Date' },
  { key: 'statusNote', label: 'Status Note' },
];

/** at-risk category -> the roster status-date field that applies to it. */
const AT_RISK_CATEGORY_DATE_FIELD = {
  warning: 'warningDate',
  pip: 'pipDate',
  resigned: 'resignedDate',
  abscond: 'abscondDate',
};

function formatDate(iso) {
  if (!iso) return '—';
  const s = String(iso);
  return s.includes('T') ? s.split('T')[0] : s.slice(0, 10);
}

function resolveStatusDate(row) {
  if (!row) return null;
  const field = AT_RISK_CATEGORY_DATE_FIELD[row.atRiskCategory];
  return field ? row[field] ?? null : null;
}

function cellValue(row, key) {
  if (!row) return '—';
  if (key === 'statusDate') {
    const v = resolveStatusDate(row);
    return v ? formatDate(v) : '—';
  }
  const v = row[key];
  if (v === undefined || v === null || String(v).trim() === '') return '—';
  if (key === 'joiningDate' || key === 'createdAt' || key === 'updatedAt') return formatDate(v);
  return v;
}

function HrRacksheetAtRisk(props) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const response = await HttpService.getRosterAtRiskSummary();
      if (response?.data?.success === false) {
        props.setSnackInfo(
          response?.data?.message || 'Failed to load at-risk headcount',
          'error'
        );
        setData(null);
        return;
      }
      setData(response?.data?.data ?? response?.data ?? null);
    } catch (error) {
      console.error('Error loading at-risk summary:', error);
      props.setSnackInfo(
        error?.response?.data?.message || 'Error loading at-risk headcount',
        'error'
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const title =
    data?.title || 'At-Risk Headcount — Warning, PIP & Imminent Exits';
  const summaryRows = Array.isArray(data?.summaryRows) ? data.summaryRows : [];
  const detailRows = Array.isArray(data?.rows) ? data.rows : [];
  const total = data?.total;

  return (
    <Box p={2}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        style={{ gap: 12 }}
        mb={2}
      >
        <Typography variant="h5" style={{ fontWeight: 700, color: MAIN_HEADER_BG, maxWidth: 900 }}>
          {title}
        </Typography>
        <Button
          variant="outlined"
          disabled={loading}
          onClick={() => load()}
          style={{ borderColor: '#F47E46', color: '#F47E46' }}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </Box>

      <Box mb={3} maxWidth={480}>
        <Paper elevation={0} style={{ border: CELL_BORDER, borderRadius: 0 }}>
          <Table size="small" style={{ borderCollapse: 'collapse' }}>
            <TableBody>
              {summaryRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} style={{ border: CELL_BORDER, padding: 12, color: '#64748b' }}>
                    No summary data
                  </TableCell>
                </TableRow>
              ) : (
                summaryRows.map((row, idx) => {
                  const stripe = idx % 2 === 0 ? ROW_STRIPE_A : ROW_STRIPE_B;
                  return (
                    <TableRow key={row.key || idx}>
                      <TableCell
                        style={{
                          border: CELL_BORDER,
                          backgroundColor: stripe,
                          fontWeight: 600,
                          color: TEXT_PRIMARY,
                          width: '72%',
                        }}
                      >
                        {row.label}
                      </TableCell>
                      <TableCell
                        align="right"
                        style={{
                          border: CELL_BORDER,
                          backgroundColor: stripe,
                          fontWeight: 700,
                          color: TEXT_PRIMARY,
                        }}
                      >
                        {row.value ?? '—'}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      <Box mb={1}>
        <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 10, color: TEXT_PRIMARY }}>
          At-Risk Detail (snapshot)
        </Typography>
        <TableContainer
          component={Paper}
          elevation={0}
          style={{ border: CELL_BORDER, borderRadius: 0, overflowX: 'auto' }}
        >
          <Table size="small" style={{ borderCollapse: 'collapse' }}>
            <TableHead>
              <TableRow>
                <TableCell style={{ ...headerCellBase, minWidth: 60 }}>Sr. No.</TableCell>
                {DETAIL_COLUMNS.map((col) => (
                  <TableCell key={col.key} style={{ ...headerCellBase, minWidth: col.key === 'name' ? 140 : 100 }}>
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {detailRows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={DETAIL_COLUMNS.length + 1}
                    align="center"
                    style={{ border: CELL_BORDER, padding: 16, color: '#64748b' }}
                  >
                    No at-risk rows
                  </TableCell>
                </TableRow>
              ) : (
                detailRows.map((row, idx) => {
                  const stripe = idx % 2 === 0 ? ROW_STRIPE_A : ROW_STRIPE_B;
                  return (
                    <TableRow key={row._id || idx}>
                      <TableCell
                        style={{
                          border: CELL_BORDER,
                          backgroundColor: stripe,
                          color: TEXT_PRIMARY,
                          textAlign: 'left',
                        }}
                      >
                        {idx + 1}
                      </TableCell>
                      {DETAIL_COLUMNS.map((col) => (
                        <TableCell
                          key={col.key}
                          style={{
                            border: CELL_BORDER,
                            backgroundColor: stripe,
                            color: TEXT_PRIMARY,
                            textAlign: 'left',
                          }}
                        >
                          {cellValue(row, col.key)}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}

export default connect(null, { setSnackInfo })(HrRacksheetAtRisk);
